/** 
 * @module DocumentsPage (Plan Verification)
 * @description Component for managing and displaying plans and supporting documents for submission.
 */
import React, { useState, useEffect, useContext } from "react";
import { Button, COLORS } from "abs-ui-components";
import { MDBRow, MDBCol } from "mdbreact";
import { MaterialReactTable } from "material-react-table";
import { Download, Delete } from "@mui/icons-material";
import { Tooltip, IconButton, Box } from "@mui/material";
import axiosHttp from "../../../services/interceptor";
import { emitToasterMsg } from "../../../services/toasterUtility";
import AbsModal from "../../../common/absModal/AbsModal";
import PlanUploadModal from "./PlanUploadModal";
import SupportingDocsUploadModal from "./SupportingDocsUploadModal";
import "./Documents.scss";
import ContainedLoader from "../../../common/Loader/ContainedLoader";
import { ClaimsContext } from "../../../services/ClaimsService";
import { FeatureType } from "../../../constants/FeatureToggles";

/**
 * @param {object} props
 * @param {object} props.planVerificationDetails
 * @param {object} props.planVerificationFlags
 * @param {boolean} props.showData
 * @param {Function} [props.onSubmittedToAbsSuccess]
 * @param {number} [props.refreshKey]
 * @param {Function} [props.refreshState]
 */
const DocumentsPage = ({
  planVerificationDetails,
  planVerificationFlags,
  showData,
  onSubmittedToAbsSuccess,
  refreshKey,
  refreshState,
}) => {
  /* ------------------------------ State ------------------------------ */
  const [documents, setDocuments] = useState([]);
  const [loader, setLoader] = useState(false);

  // Upload modals + selections
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isSupportingDocsModalOpen, setIsSupportingDocsModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedSupportingFiles, setSelectedSupportingFiles] = useState([]);

  const tabName = "Documents";
  const verificationStatus = planVerificationDetails?.VerificationStatus?.toLowerCase() || "";

  const { userHasFeature } = useContext(ClaimsContext);

  // Enable upload buttons in Draft/Retained, or Under Review with corrections
  const showDocumentButtons =
    ["draft", "retained"].includes(verificationStatus) ||
    (verificationStatus === "under review" && !!planVerificationFlags?.data?.hasCorrections);

  /* ------------------------------ Effects ------------------------------ */
  useEffect(() => {
    if (planVerificationDetails?.PlanId) {
      fetchDocuments();
    }
  }, [planVerificationDetails?.PlanId, planVerificationDetails?.WcnNumber, refreshKey]);

  /* ------------------------------ UI Toggles ------------------------------ */

  const onSelectFilesEvent = () => {
    setIsUploadModalOpen(!isUploadModalOpen);
    if (isUploadModalOpen) setSelectedFiles([]);
  };

  const onSelectSupportingFilesEvent = () => {
    setIsSupportingDocsModalOpen(!isSupportingDocsModalOpen);
    if (isSupportingDocsModalOpen) setSelectedSupportingFiles([]);
  };

  /* ------------------------------ Actions ------------------------------ */

  const handleUploadSupportingDocClick = () => {
    if (!selectedSupportingFiles || selectedSupportingFiles.length === 0) {
      emitToasterMsg("error", "No files uploaded.");
      return;
    }
    if (!planVerificationDetails?.PlanId) {
      emitToasterMsg("error", "Missing PlanId.");
      return;
    }

    const maxSizeBytes = 50 * 1024 * 1024; // 50 MB
    const invalidFiles = selectedSupportingFiles.filter((file) => file.size > maxSizeBytes);
    if (invalidFiles.length > 0) {
      emitToasterMsg("error", "One or more files exceed the 50 MB limit.");
      return;
    }

    const formData = new FormData();
    formData.append("PlanId", String(planVerificationDetails.PlanId));
    formData.append("IsVerification", "false");
    selectedSupportingFiles.forEach((file) => formData.append("Files", file));

    const params = new URLSearchParams();
    if (planVerificationDetails?.WcnNumber) params.set("wcnNumber", planVerificationDetails.WcnNumber);

    const API = `/api/PlanVerification/UploadDocuments`;
    setLoader(true);

    axiosHttp
      .post(`${API}?${params.toString()}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response?.data?.IsSuccess === false) {
          emitToasterMsg(
            "error",
            response?.data?.Message || "An error occurred while uploading supporting documents."
          );
        } else {
          emitToasterMsg("success", response?.data?.Message || "Supporting documents uploaded successfully");
          fetchDocuments();
          setIsSupportingDocsModalOpen(false);
        }
        setLoader(false);
      })
      .catch((error) => {
        setLoader(false);
        emitToasterMsg("error", `Error: ${error.response?.data?.Message || error.message}`);
      });
  };

  const uploadPlan = (files) => {
    if (!files || files.length === 0) {
      emitToasterMsg("error", "No file uploaded.");
      return;
    }
    if (files.length > 1) {
      emitToasterMsg("error", "Only one plan is permitted for upload at a time.");
      return;
    }

    const file = files[0];
    if (file.type !== "application/pdf") {
      emitToasterMsg("error", "Only PDF files are allowed for plans.");
      setSelectedFiles([]);
      return;
    }
    const maxSizeBytes = 50 * 1024 * 1024; // 50 MB
    if (file.size > maxSizeBytes) {
      emitToasterMsg("error", "File size exceeds 50 MB limit.");
      return;
    }

    const formData = new FormData();
    formData.append("PlanId", String(planVerificationDetails.PlanId));
    formData.append("File", file);
    formData.append("IsVerification", "false");

    const wcnNumber = planVerificationDetails?.WcnNumber;
    const params = new URLSearchParams();
    if (wcnNumber) params.set("wcnNumber", wcnNumber);
    const API = `/api/PlanVerification/UploadPlan`;

    setLoader(true);

    axiosHttp
      .post(`${API}?${params.toString()}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        const { data } = response || {};
        if (data?.isSuccess === true) {
          emitToasterMsg("success", data?.Message || "Plan uploaded successfully");
          fetchDocuments();
          refreshState?.();
        } else {
          emitToasterMsg("error", data?.Message || "An error occurred while uploading the plan.");
        }
        setLoader(false);
      })
      .catch((error) => {
        setLoader(false);
        emitToasterMsg("error", `Error: ${error.response?.data?.Message || error.message}`);
      });
  };

  const handleUploadPlanClick = () => {
    uploadPlan(selectedFiles);
    setSelectedFiles([]);
  };

  const handleSubmitToAbs = async () => {
    const planId = planVerificationDetails?.PlanId;
    const wcnNumber = planVerificationDetails?.WcnNumber;
    const planVerificationStatus = planVerificationDetails?.VerificationStatus;

    if (!planId) {
      emitToasterMsg("error", "Missing required parameter: planId");
      return;
    }

    setLoader(true);
    try {
      const params = new URLSearchParams();
      params.set("planId", String(planId));
      if (wcnNumber) params.set("wcnNumber", wcnNumber);
      if (planVerificationStatus === "Retained" || planVerificationStatus === "Under Review") {
        params.set("isReSubmit", "true");
      }

      const API = `/api/PlanVerification/SubmitToAbs`;
      const response = await axiosHttp.get(`${API}?${params.toString()}`);

      if (response?.status === 200) {
        const { IsSuccess, isSuccess, Message, message } = response?.data || {};
        const success = IsSuccess !== false && (isSuccess === undefined || isSuccess);
        if (success) {
          emitToasterMsg("success", Message || message || "Submitted to ABS successfully");
          fetchDocuments();
          onSubmittedToAbsSuccess?.();
        } else {
          emitToasterMsg("error", Message || message || "Submission failed");
        }
      } else {
        emitToasterMsg("error", `Internal ERP Error: ${response?.data?.Message || "Unknown error"}`);
      }
    } catch (error) {
      emitToasterMsg("error", `Error: ${error.response?.data?.Message || error.message}`);
    } finally {
      await refreshState?.();
      setLoader(false);
    }
  };

  /* ------------------------------ Data Fetchers ------------------------------ */

  const fetchDocuments = async () => {
    if (!planVerificationDetails?.PlanId) {
      emitToasterMsg("error", "Missing planId for fetching documents");
      return;
    }

    try {
      const params = new URLSearchParams();
      params.set("planId", String(planVerificationDetails.PlanId));
      params.set("isVerification", "false");
      if (planVerificationDetails?.WcnNumber) {
        params.set("wcnNumber", planVerificationDetails.WcnNumber);
      }

      const API = `/api/PlanVerification/GetDocuments`;
      const fullUrl = `${API}?${params.toString()}`;
      const response = await axiosHttp.get(fullUrl);

      if (response.status === 200) {
        if (response?.data?.isSuccess) {
          const docsData = response.data.data || [];
          if (docsData.length === 0) {
            setDocuments([]);
            emitToasterMsg("info", "No documents found for the specified criteria");
          } else {
            setDocuments(
              docsData.map((doc) => ({
                ...doc,
                submittedDate: doc.submittedDate
                  ? new Date(doc.submittedDate).toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }) || "Invalid Date"
                  : "",
                verifiedDate: doc.verifiedDate
                  ? new Date(doc.verifiedDate).toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }) || "Invalid Date"
                  : "",
              }))
            );
          }
        } else {
          setDocuments([]);
          emitToasterMsg("error", response?.data?.Message || "Failed to retrieve documents");
        }
      } else {
        emitToasterMsg("error", `Internal ERP Error: ${response?.data?.Message || "Unknown error"}`);
      }
    } catch (error) {
      emitToasterMsg(
        "error",
        `Error${tabName ? ` [${tabName}]` : ""}: ${error.response?.data?.Message || error.message}'`
      );
      setDocuments([]);
    }
  };

  /* ------------------------------ Download/Delete ------------------------------ */

  const handleDownload = async (documentMstId, fileName) => {
    const planId = planVerificationDetails?.PlanId;
    if (!planId || !documentMstId) {
      emitToasterMsg("error", "Missing required parameters for download.");
      return;
    }

    setLoader(true);
    try {
      const params = new URLSearchParams();
      params.set("planId", String(planId));
      params.set("documentMstId", String(documentMstId));
      if (planVerificationDetails?.WcnNumber) {
        params.set("WcnNumber", String(planVerificationDetails.WcnNumber));
      }

      const API = `/api/PlanVerification/DownloadDocument`;
      const response = await axiosHttp.get(`${API}?${params.toString()}`, { responseType: "blob" });

      if (response.status === 200) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName || "document");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        emitToasterMsg("success", "Document downloaded successfully");
      } else {
        emitToasterMsg("error", `Internal ERP Error: ${response?.data?.Message || "Unknown error"}`);
      }
    } catch (error) {
      emitToasterMsg("error", `Error: ${error.response?.data?.Message || error.message}`);
    } finally {
      setLoader(false);
    }
  };

  const handleDelete = async (documentMstId) => {
    const planId = planVerificationDetails?.PlanId;
    if (!planId || !documentMstId) {
      emitToasterMsg("error", "Error Deleting Document: Missing required parameters.");
      return;
    }

    setLoader(true);
    try {
      const params = new URLSearchParams();
      params.set("planId", String(planId));
      params.set("documentMstId", String(documentMstId));
      if (planVerificationDetails?.WcnNumber) {
        params.set("wcnNumber", planVerificationDetails.WcnNumber);
      }

      const API = `/api/PlanVerification/DeleteDocument`;
      const response = await axiosHttp.delete(`${API}?${params.toString()}`);

      if (response.status === 200) {
        const { IsSuccess, Message, isSuccess, message } = response.data || {};
        if (IsSuccess !== false && (isSuccess === undefined || isSuccess)) {
          emitToasterMsg("success", Message || message || "Document deleted successfully");
          fetchDocuments();
        } else {
          emitToasterMsg("error", Message || message || "Failed to delete document");
        }
      } else {
        emitToasterMsg("error", `Internal ERP Error: ${response?.data?.Message || "Unknown error"}`);
      }
    } catch (error) {
      emitToasterMsg("error", `Error: ${error.response?.data?.Message || error.message}`);
    } finally {
      setLoader(false);
      refreshState?.();
    }
  };

  /* ------------------------------ Table Columns ------------------------------ */

  const columns = React.useMemo(
    () => [
      { accessorKey: "revision", header: "Revision", size: 80 },
      { accessorKey: "fileName", header: "File Name", size: 150 },
      { accessorKey: "status", header: "Status", size: 120 },
      { accessorKey: "submittedDate", header: "Submitted Date", size: 150 },
      { accessorKey: "verifiedDate", header: "Verified Date", size: 150 },
      {
        accessorKey: "action",
        header: "Action",
        size: 20,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => (
          <Box>
            <Tooltip title="Download">
              <span>
                <IconButton
                  color="info"
                  onClick={() => handleDownload(row.original.documentDtlId, row.original.fileName)}
                  disabled={!row.original.showDownloadButton}
                >
                  <Download
                    sx={{ color: row.original.showDownloadButton ? "#0645AD" : "action.disabled" }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete">
              <span>
                <IconButton
                  color="error"
                  onClick={() => handleDelete(row.original.documentDtlId)}
                  disabled={!row.original.showDeleteButton}
                >
                  <Delete
                    sx={{ color: row.original.showDeleteButton ? "error.main" : "action.disabled" }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [handleDownload, handleDelete, showDocumentButtons]
  );

  /* ------------------------------ Render ------------------------------ */

  const content = (
		<>
			{showData}
			<MDBRow className="mt-4">
				<MDBCol>
					<MDBRow className="mb-4">
						<MDBCol>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<div>
									<Button
										onClick={() => setIsUploadModalOpen(true)}
										primaryBackgroundColor={COLORS.BLUE.DARK}
										style={{ marginRight: "10px" }}
										disabled={!showDocumentButtons}
									>
										Upload Plan
									</Button>
									<Button onClick={() => setIsSupportingDocsModalOpen(true)} primaryBackgroundColor={COLORS.BLUE.DARK}>
										Upload Supporting Documents
									</Button>
								</div>
								<div>
									{userHasFeature?.(FeatureType.SubmitToAbs) && (
										<Button
											onClick={handleSubmitToAbs}
											primaryBackgroundColor={COLORS.BLUE.DARK}
											disabled={!planVerificationFlags?.data?.showSubmitToAbs}
										>
											Submit to ABS
										</Button>
									)}
								</div>
							</div>
						</MDBCol>
					</MDBRow>
					<MDBRow>
						<MDBCol>
							<MaterialReactTable
								columns={columns}
								data={documents}
								enableTopToolbar={false}
								enableBottomToolbar={false}
								enableColumnFilters={false}
								enableSorting={false}
								enablePagination={false}
								enableRowSelection={false}
								enableExpanding={false}
								muiTableContainerProps={{ sx: { maxHeight: "none" } }}
								renderEmptyRowsFallback={() => (
									<Box
										sx={{
											width: "100%",
											py: 3,
											textAlign: "center",
											fontSize: "1rem",
                      fontStyle: "italic",
                      fontweight:400,
                      lineheight: "1.5rem",
                      letterspacing: "0.00938em",
										}}
									>
										No records to display
									</Box>
								)}
							/>
						</MDBCol>
					</MDBRow>
				</MDBCol>
			</MDBRow>

			{isUploadModalOpen && (
				<AbsModal isOpen={isUploadModalOpen} title="Upload Plan" onClose={setIsUploadModalOpen} close={onSelectFilesEvent}>
					<PlanUploadModal
						handleUploadClick={handleUploadPlanClick}
						toggleModal={onSelectFilesEvent}
						toggleClose={onSelectFilesEvent}
						selectedFiles={selectedFiles}
						setSelectedFiles={setSelectedFiles}
					/>
				</AbsModal>
			)}

			{isSupportingDocsModalOpen && (
				<AbsModal
					isOpen={isSupportingDocsModalOpen}
					title="Upload Supporting Documents"
					onClose={setIsSupportingDocsModalOpen}
					close={onSelectSupportingFilesEvent}
				>
					<SupportingDocsUploadModal
						handleUploadClick={handleUploadSupportingDocClick}
						toggleModal={onSelectSupportingFilesEvent}
						toggleClose={onSelectSupportingFilesEvent}
						selectedFiles={selectedSupportingFiles}
						setSelectedFiles={setSelectedSupportingFiles}
					/>
				</AbsModal>
			)}
		</>
  );

  return (
    <div className={`documents-page ${loader ? "is-loading" : ""}`}>
      <div className="documents-content">{content}</div>
      {loader && (
        <div className="documents-loader" aria-busy="true" role="status" aria-live="polite">
          <ContainedLoader />
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;